<template>
  <Simulation
    v-if="smallScreen"
    class="block mb-4"
    :price="product.price"
    :productCategoryValue="category"
    :offeredMonths="offeredMonths"
    :contract-type="contractType"
    :productless="productless"
    :virtualProduct="isVirtualProduct"
    :advisoryAnswer="advisoryAnswer"
    @recommendedOffer="handleRecommendedOffer"
  />
  <div>
    <div>
      <SubscriptionProgressBar
        :stepKeys="stepKeys"
        :activeStepIndex="activeStepIndex"
        :maxReachableStepIndex="maxReachableStepIndex"
        :category="category"
        :productless="productless"
        @goToStepKey="goToStepKey"
        @reset="resetAttributes"
      />
      <div class="sm:inline lg:flex">
        <div class="lg:w-4/5 lg:mr-4">
          <Transition :name="stepTransitionName" mode="out-in">
            <SubscriptionStepContent
              :key="activeStepKey"
              :activeStepKey="activeStepKey"
              :sellerCompleteName="sellerCompleteName"
              :product="product"
              :advisoryAnswer="advisoryAnswer"
              :customer="customer"
              :address="address"
              :category="category"
              :categories="categories"
              :availableOfferTypes="availableOfferTypes"
              :contractType="contractType"
              :productless="productless"
              :allowVirtualProduct="allowVirtualProduct"
              @selectedSeller="handleSelectedSeller"
              @update:product="subscription.product = $event"
              @update:advisoryAnswer="subscription.advisoryAnswer = $event"
              @update:customer="subscription.customer = $event"
              @update:address2="address.address2 = $event"
              @newAddress="newAddress"
              @continue="goNext"
              @cancel="resetAttributes"
              @eligibilityContinue="handleEligibilityContinue"
              @edit="handleSummaryEdit"
            />
          </Transition>
        </div>
        <div class="lg:w-2/5 mt-4 lg:mt-0">
          <div v-if="category">
            <ContractDetailsSection :categories="categories" :productCategoryValue="category" />
            <Simulation
              class="mt-4"
              :price="product.price"
              :productCategoryValue="category"
              :offeredMonths="offeredMonths"
              :contract-type="contractType"
              :productless="productless"
              :virtualProduct="isVirtualProduct"
              :advisoryAnswer="advisoryAnswer"
              @recommendedOffer="handleRecommendedOffer"
              @update:productIsInsurable="productIsInsurable = $event"
            />
            <FormButtons
              v-if="activeStepKey === 'actions'"
              @sendTextMessage="handleSendTextMessage"
              @paymentInScreen="handlePaymentInScreen"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <SubscriptionModals
    ref="modals"
    :subscription="subscription"
    @sendFailedPaymentLink="handleSendFailedPaymentLink"
    @closePaymentModal="handleClosePaymentModal"
    @updateOffer="handleUpdateOffer"
  />
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";

import SubscriptionStepContent from "@/components/pos/subscription/formwithsteps/SubscriptionStepContent.vue";
import SubscriptionProgressBar from "@/components/pos/subscription/formwithsteps/SubscriptionProgressBar.vue";
import FormButtons from "@/components/pos/subscription/formwithsteps/FormButtons.vue";
import SubscriptionModals from "@/components/pos/subscription/formwithsteps/SubscriptionModals.vue";
import Simulation from "@/components/pos/subscription/formwithsteps/Simulation.vue";
import ContractDetailsSection from "@/components/pos/subscription/form/ContractDetailsSection.vue";

import { EventBus } from "@/utils/event_bus";
import { screenSize, screens } from "@/utils/breakpoints.js";
import {
  isMobileProtection as isMobileProtectionUtil,
  isVirtualProduct as isVirtualProductUtil,
  isPresent,
} from "@estaly/ui";
import { isAdvisoryStepComplete, isCustomerStepComplete } from "@/utils/subscriptionStepChecks";
import { createInitialSubscription } from "./formwithsteps/subscriptionDefaults";
import { buildLeadParams } from "./formwithsteps/subscriptionLeadParams";

export default {
  components: {
    SubscriptionStepContent,
    SubscriptionProgressBar,
    FormButtons,
    ContractDetailsSection,
    Simulation,
    SubscriptionModals,
  },
  props: {
    categories: {
      type: Array,
      required: true,
    },
    offeredMonths: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      subscription: createInitialSubscription(),
      sellerCompleteName: "",
      contractType: null,
      productless: null,
      allowVirtualProduct: false,
      activeStepIndex: 0,
      stepTransitionName: "step-slide-left",
      eligibilityConfirmed: false,
      productIsInsurable: true,
    };
  },

  computed: {
    ...mapState("store", ["selectedStoreId"]),
    ...mapGetters("store", ["selectedStore"]),
    ...mapGetters("auth", ["currentUserHasPermission", "isSellerOrResponsible"]),

    product() {
      return this.subscription.product;
    },
    advisoryAnswer() {
      return this.subscription.advisoryAnswer || {};
    },
    category() {
      return this.subscription.category;
    },
    customer() {
      return this.subscription.customer;
    },
    address() {
      return this.subscription.address;
    },

    smallScreen() {
      return screenSize() < screens.lg;
    },

    productRequired() {
      return !this.productless;
    },

    isVirtualProduct() {
      return isVirtualProductUtil(this.product);
    },

    isMobileProtection() {
      return isMobileProtectionUtil(this.category);
    },

    availableOfferTypes() {
      const mpcpTypes = ["mobile_protection", "connect_protection"];
      return this.categories.map((c) => c.productCategoryName).filter((name) => mpcpTypes.includes(name));
    },

    stepKeys() {
      const keys = ["seller"];
      keys.push("advisory");
      if (this.productRequired) keys.push("product");
      if (this.isMobileProtection) keys.push("eligibility");
      keys.push("customer", "actions");
      return keys;
    },

    activeStepKey() {
      return this.stepKeys[this.activeStepIndex] || "seller";
    },

    eligibilityStepComplete() {
      return this.eligibilityConfirmed;
    },

    maxReachableStepIndex() {
      const maxByData = (() => {
        if (!this.subscription.sellerId) return this.stepKeys.indexOf("seller");
        if (!this.advisoryStepComplete) return this.stepKeys.indexOf("advisory");
        if (!this.productStepComplete) return this.stepKeys.indexOf("product");
        if (this.isMobileProtection && !this.eligibilityStepComplete) return this.stepKeys.indexOf("eligibility");
        if (!this.customerStepComplete) return this.stepKeys.indexOf("customer");
        return this.stepKeys.length - 1;
      })();
      return Math.max(0, maxByData);
    },

    productStepComplete() {
      if (!this.productRequired) return true;
      if (isVirtualProductUtil(this.product)) {
        return (
          isPresent(this.product?.referenceId) &&
          isPresent(this.product?.lineHolderName) &&
          isPresent(this.product?.transactionDate)
        );
      }
      return isPresent(this.product?.price) && this.productIsInsurable;
    },

    advisoryStepComplete() {
      return isAdvisoryStepComplete(this.advisoryAnswer);
    },

    customerStepComplete() {
      return isCustomerStepComplete(this.customer, this.address, this.advisoryAnswer);
    },
  },

  watch: {
    stepKeys: {
      handler(newKeys) {
        if (this.activeStepIndex > newKeys.length - 1) {
          this.activeStepIndex = Math.max(0, newKeys.length - 1);
        }
      },
      immediate: true,
    },
    "subscription.sellerId"(newVal) {
      if (!newVal) {
        this.activeStepIndex = 0;
      }
    },
    "subscription.advisoryAnswer.usageType": {
      handler(usageType) {
        this.customer.professionalCustomer = usageType === "professional";

        if (usageType === "personal") {
          this.customer.sirenNumber = "";
          this.customer.companyName = "";
          this.customer.legalForm = "";
        } else if (usageType === "professional") {
          this.customer.birthDate = "";
          this.customer.birthCity = "";
          this.customer.birthCountry = "";
          this.customer.nationality = "";
        }
      },
      immediate: true,
    },
    "subscription.advisoryAnswer.offerType": {
      handler(offerType) {
        this.eligibilityConfirmed = false;
        this.subscription.advisoryAnswer.deviceBoughtFromDistributor = true;

        if (offerType) {
          const correspondingCategory = this.categories.find((cat) => cat.productCategoryName === offerType);
          if (correspondingCategory) {
            this.subscription.category = offerType;
            this.contractType = correspondingCategory.contractType;
            this.productless = correspondingCategory.contractDistribution?.productless ?? false;
            this.allowVirtualProduct = correspondingCategory.contractDistribution?.allowVirtualProduct ?? false;
          }
        } else {
          this.subscription.category = null;
          this.contractType = null;
          this.productless = null;
          this.allowVirtualProduct = false;
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("store", ["withLoader"]),
    ...mapActions("leads", ["create"]),

    handleSelectedSeller(seller) {
      this.sellerCompleteName = seller ? seller.name : "";
      this.subscription.sellerId = seller ? seller.id : "";
      if (seller && this.activeStepKey === "seller") {
        this.goNext();
      }
    },

    handleEligibilityContinue(option) {
      this.subscription.advisoryAnswer = {
        ...this.advisoryAnswer,
        deviceBoughtFromDistributor: option !== "other_professional",
      };
      this.eligibilityConfirmed = true;
      this.goNext();
    },

    handleRecommendedOffer(payload) {
      const title = payload?.title ?? null;
      const id = payload?.id ?? null;
      const currentTitle = this.advisoryAnswer?.recommendedOffer;
      const currentId = this.advisoryAnswer?.recommendedOfferId;
      if (currentTitle === title && currentId === id) return;
      this.subscription.advisoryAnswer = {
        ...this.advisoryAnswer,
        recommendedOffer: title,
        recommendedOfferId: id,
      };
    },
    handleSendTextMessage() {
      this.subscription.invitationSentByEstaly = true;
      this.submitForm();
    },

    handlePaymentInScreen() {
      this.subscription.invitationSentByEstaly = false;
      this.subscription.paymentInStorePlatform = true;
      this.submitForm();
    },

    handleClosePaymentModal(shouldReset) {
      if (shouldReset) {
        this.resetAttributes();
      } else {
        this.$refs.modals.reset();
      }
    },

    handleUpdateOffer({ index, offer }) {
      this.subscription.offers = [
        ...this.subscription.offers.slice(0, index),
        offer,
        ...this.subscription.offers.slice(index + 1),
      ];
    },

    async handleSendFailedPaymentLink() {
      this.subscription.invitationSentByEstaly = true;
      this.subscription.paymentInStorePlatform = false;
      await this.submitForm();
    },

    async submitForm() {
      if (isPresent(this.address.address1) && isPresent(this.address.city) && isPresent(this.address.country)) {
        await this.handleLeadCreation();
      } else {
        this.address.addressError = true;
      }
    },

    async handleLeadCreation() {
      const params = buildLeadParams(this.subscription, this.productRequired);

      await this.withLoader(async () => {
        const data = await this.create({
          storeId: this.selectedStoreId,
          params,
          options: { timeout: 30_000 },
        });
        if (!data) return;

        if (this.subscription.paymentInStorePlatform) {
          Object.assign(this.subscription, {
            id: data.lead.id,
            estalyId: data.lead.estalyId,
            offers: data.lead.offers,
            discount: data.lead.discount,
            advisoryAgreement: data.lead.advisoryAgreement,
            informationNoticeLink: data.lead.informationNoticeLink,
            productInformationSheetLink: data.lead.productInformationSheetLink,
          });
          this.$refs.modals.openContractConsultation();
          this.subscription.paymentInStorePlatform = false;
        } else {
          this.notify({
            category: "simple",
            type: "success",
            title: "Lien de paiement envoyé",
            text: "Envoyé au client par SMS et par e-mail.",
          });
          this.resetAttributes();
        }
      });
    },

    newAddress(value) {
      this.subscription.address = value;
      if (
        isPresent(this.subscription.address.address1) &&
        isPresent(this.subscription.address.city) &&
        isPresent(this.subscription.address.country)
      ) {
        this.subscription.address.addressError = false;
      }
    },

    resetAttributes() {
      this.subscription = createInitialSubscription();
      this.$refs.modals.reset();
      this.sellerCompleteName = "";
      this.contractType = null;
      this.productless = null;
      this.allowVirtualProduct = false;
      this.activeStepIndex = 0;
      this.eligibilityConfirmed = false;
      this.productIsInsurable = true;

      EventBus.emit("resetAddress");
      EventBus.emit("resetSelectedProductCategory");
    },

    goToStepIndex(idx) {
      const safeIdx = Math.max(0, Math.min(idx, this.stepKeys.length - 1));
      this.setActiveStepIndex(safeIdx);
    },

    goToStepKey(stepKey) {
      const idx = this.stepKeys.indexOf(stepKey);
      if (idx >= 0 && idx <= this.maxReachableStepIndex) {
        this.goToStepIndex(idx);
      }
    },

    handleSummaryEdit(stepKey) {
      // Redirect category to advisory since category is now derived from offerType
      if (stepKey === "category") {
        this.goToStepKey("advisory");
        return;
      }
      // If product step doesn't exist (productless offer), redirect to advisory.
      if (stepKey === "product" && !this.stepKeys.includes("product")) {
        this.goToStepKey("advisory");
        return;
      }
      this.goToStepKey(stepKey);
    },

    canGoNext() {
      switch (this.activeStepKey) {
        case "seller":
          return isPresent(this.subscription.sellerId);
        case "product":
          return this.productStepComplete;
        case "advisory":
          return this.advisoryStepComplete;
        case "eligibility":
          return this.eligibilityStepComplete;
        case "customer":
          return this.customerStepComplete;
        default:
          return false;
      }
    },

    goNext() {
      if (!this.canGoNext()) return;
      const nextIdx = Math.min(this.activeStepIndex + 1, this.stepKeys.length - 1);
      this.setActiveStepIndex(nextIdx);
    },

    setActiveStepIndex(nextIdx) {
      this.stepTransitionName = nextIdx > this.activeStepIndex ? "step-slide-left" : "step-slide-right";
      this.activeStepIndex = nextIdx;
    },
  },
};
</script>

<style scoped>
.step-slide-left-enter-active,
.step-slide-left-leave-active,
.step-slide-right-enter-active,
.step-slide-right-leave-active {
  transition:
    transform 300ms ease,
    opacity 250ms ease;
}

.step-slide-left-enter-from,
.step-slide-right-leave-to {
  transform: translateX(24px);
  opacity: 0;
}

.step-slide-left-leave-to,
.step-slide-right-enter-from {
  transform: translateX(-24px);
  opacity: 0;
}
</style>
